import type {
  Product,
  SelectedOptions,
} from "@/configurator/domain/models/types";
import type { OfferGroup } from "@/configurator/domain/models/view";
import { getSelectedOptionIds } from "./selection";

/**
 * The specification table of the offer: every option group with the option(s)
 * selected in it right now.
 *
 * Derived here rather than in the PDF generator because it is the same mapping
 * question the renderer commands answer — what does the current selection
 * resolve to — and the document should not be re-deriving it from group kinds
 * and default ids.
 */
export function buildOfferGroups(
  product: Product | undefined,
  selected: SelectedOptions,
): OfferGroup[] {
  if (!product) return [];

  const currency = product.basePrice.currency;

  return product.optionGroups
    .map((group) => {
      const selectedIds = getSelectedOptionIds(group, selected);
      const rows = group.options
        // `group.options` order, not selection order: the document should read
        // the same way twice for the same configuration.
        .filter((option) => selectedIds.includes(option.id))
        .map((option) => ({
          optionId: option.id,
          label: option.label,
          color: "color" in option ? option.color : undefined,
          // The swatch in the panel is painted with `texture`, so the offer has
          // to reach for the same asset first — falling back to `image` keeps
          // configs that only ship a product photo working.
          image:
            ("texture" in option ? option.texture : undefined) ?? option.image,
          surcharge:
            option.price && option.price > 0
              ? { amount: option.price, currency }
              : undefined,
        }));

      return { id: group.id, label: group.label, rows };
    })
    .filter((group) => group.rows.length > 0);
}
